import { formatDistance, formatDuration, getLang, type I18nKey, tr } from '@app/i18n'
import { localized } from '@app/rvf'
import { backToTarget, setConnectorPref } from '../actions'
import { extractSteps } from '../nav/route-progress'
import { enterOrient } from '../orient'
import {
  anchorId,
  type ConnectorPref,
  externalLeg,
  langTick,
  routePlan,
  routePref,
  targetId,
  venue,
} from '../state'
import { featureName, planCrossesLevels, planLevelsLabel } from './labels'

const CONNECTORS: readonly { pref: ConnectorPref; label: I18nKey; note: I18nKey }[] = [
  { pref: 'escalator', label: 'connector.escalator', note: 'connector.escalator_note' },
  { pref: 'lift', label: 'connector.lift', note: 'connector.lift_note' },
  { pref: 'stairs', label: 'connector.stairs', note: 'connector.stairs_note' },
]

/** Выбор способа подъёма — только на мультиэтажных площадках. */
function ConnectorRow() {
  const active = routePref.value
  return (
    <div class="conn-row">
      {CONNECTORS.map((c) => (
        <button
          key={c.pref}
          type="button"
          class={active === c.pref ? 'conn-btn conn-on' : 'conn-btn'}
          onClick={() => setConnectorPref(c.pref)}
        >
          <span class="conn-label">{tr(c.label)}</span>
          <span class="conn-note">{tr(c.note)}</span>
        </button>
      ))}
    </div>
  )
}

/** Часы прибытия «13:28» — местное время устройства + время в пути. */
function arrivalClock(durationS: number): string {
  const at = new Date(Date.now() + durationS * 1000)
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
}

/** Откуда стартуем: имя QR-щита, иначе честное «вы здесь». */
function originLabel(): string {
  const v = venue.value
  const anchor = v?.anchors.find((a) => a.id === anchorId.value)
  const name = anchor && localized(anchor.name, getLang(), v?.venue.default_language ?? 'en')
  return name || tr('map.you_are_here')
}

/**
 * Шапка «откуда → куда» (ScreenRoute прототипа): колонка точек — path-круг
 * старта, линия, амбер-квадрат цели — и две строки имён.
 */
function RouteEnds({ title }: { title: string }) {
  return (
    <div class="rp-ends glass">
      <div class="rp-dots" aria-hidden="true">
        <span class="rp-dot-start" />
        <span class="rp-dot-line" />
        <span class="rp-dot-end" />
      </div>
      <div class="rp-names">
        <span>{originLabel()}</span>
        <span>{title}</span>
      </div>
    </div>
  )
}

/** Панель превью маршрута: прибытие — гигантом, «В путь» — с сиянием. */
export function RoutePanel() {
  langTick.value
  const v = venue.value
  const plan = routePlan.value
  if (!v || !plan) return null
  const id = targetId.value
  const title = id ? featureName(v, id) : tr('follow.back_route')
  const levels = planLevelsLabel(v, plan)
  const totalS = plan.durationS + (externalLeg.value?.durationS ?? 0)
  const turns = extractSteps(plan.polyline).length

  return (
    <>
      <div class="rp-top">
        <button
          type="button"
          class="lang-btn"
          aria-label={tr('common.back')}
          onClick={backToTarget}
        >
          ←
        </button>
        <RouteEnds title={title} />
      </div>
      <section class="sheet">
        <div class="rp-arrival">
          <div>
            <div class="rp-arrival-label">{tr('route.arrival_at')}</div>
            <div class="rp-arrival-time tabular weight-breathe">{arrivalClock(totalS)}</div>
            <div class="rp-arrival-sub">
              {tr('route.remaining')} <b>{formatDuration(totalS)}</b> ·{' '}
              {formatDistance(plan.distanceM)}
              {levels ? ` · ${levels}` : ''}
            </div>
            {externalLeg.value && (
              <div class="rp-arrival-sub">
                {tr('route.ext_leg', { min: String(Math.ceil(externalLeg.value.durationS / 60)) })}
              </div>
            )}
          </div>
          {turns > 0 && (
            <span class="pill pill-path">{tr('route.turns_n', { n: String(turns) })}</span>
          )}
        </div>
        {planCrossesLevels(v, plan) && id && <ConnectorRow />}
        <button type="button" class="cta-hero cta-hero-wide light-sweep" onClick={enterOrient}>
          <span class="cta-hero-label">{tr('route.go')}</span>
          <span class="cta-hero-arrow" aria-hidden="true">
            →
          </span>
        </button>
      </section>
    </>
  )
}
